import { createContext, memo, useCallback, useContext, useMemo, useState } from 'react'
import { applyNodeChanges, Background, Controls, Handle, Panel, Position, ReactFlow } from '@xyflow/react'
import { Empty, Radio, Spin } from 'antd'

import '@xyflow/react/dist/style.css'
import './Tree.css'

import { allowedDropTargets, findDropTarget, subtreeIds } from './treeDrag'
import { layoutForest } from './treeLayout'

import type { ReactNode } from 'react'
import type { Node, NodeChange, NodeProps, OnNodeDrag } from '@xyflow/react'
import type { TreeDirection, TreeItemBase } from './treeLayout'

export type { TreeDirection, TreeItemBase } from './treeLayout'

/**
 * Клас маркера перетягування. Домен ставить його на свій елемент картки й
 * передає селектор у `dragHandleSelector`: тягнути картку можна лише за нього,
 * а решта картки лишається клікабельною.
 */
export const TREE_DRAG_HANDLE_CLASS = 'wl-tree-drag-handle'

/** Те, що вузол знає про своє місце в дереві (домен малює за цим бейджі/кнопки). */
export interface TreeNodeMeta {
    isRoot: boolean
    hasChildren: boolean
    childrenCount: number
    direction: TreeDirection
    /** Вузол можна тягнути (є `onMove` і `canDrag` дозволив) — домен малює маркер. */
    isDraggable: boolean
    /** Цю картку зараз тягнуть. */
    isDragged: boolean
    /** Скільки вузлів їде разом із перетягнутою карткою (вона сама + нащадки). */
    dragCount: number
    /** Картка лягла на цей вузол, і відпускання зробить його батьком. */
    isDropTarget: boolean
}

export interface TreeProps<T extends TreeItemBase> {
    items: T[]
    /** Вміст картки вузла — чистий доменний рендер (без обв'язки xyflow). */
    renderNode: (item: T, meta: TreeNodeMeta) => ReactNode
    isLoading?: boolean
    /** Розмір картки; від нього рахується розкладка, тож він має збігатися з версткою. */
    nodeWidth?: number
    nodeHeight?: number
    height?: string
    defaultDirection?: TreeDirection
    emptyText?: string
    /**
     * Перенесення вузла під інший. Без нього полотно поводиться як раніше:
     * вузли не тягаються (ADR 028). Позиції після відпускання повертаються до
     * розкладки — нову структуру домен приносить перечитуванням.
     */
    onMove?: (item: T, target: T) => void
    /** Чи можна тягнути цей вузол (права, головний вузол). Типово — можна. */
    canDrag?: (item: T) => boolean
    /**
     * Додаткова доменна умова цілі. Себе, нащадків і нинішнього батька Tree
     * відкидає сам — ціллю вони не підсвічуються ніколи.
     */
    canDrop?: (item: T, target: T) => boolean
    /** CSS-селектор маркера, за який тягнуть картку (типово — `TREE_DRAG_HANDLE_CLASS`). */
    dragHandleSelector?: string
}

interface TreeRenderState {
    render: (itemId: string, direction: TreeDirection, isRoot: boolean) => ReactNode
    childrenCount: Record<string, number>
}

/** Стан перетягування: хто їде, куди можна, на кого зараз лягла картка. */
interface DragState {
    id: string
    subtree: Set<string>
    allowed: Set<string>
    targetId: string | null
}

/**
 * Рендер вузла їде контекстом, а не через `data`: nodeTypes мають бути
 * стабільними між рендерами (інакше xyflow перемонтовує все дерево), а
 * доменна функція рендеру змінюється щоразу.
 */
const TreeRenderContext = createContext<TreeRenderState | null>(null)

interface DomainNodeData extends Record<string, unknown> {
    itemId: string
    direction: TreeDirection
    isRoot: boolean
}

/** Обв'язка xyflow: точки з'єднання + доменний вміст. */
const DomainNode = memo(({ data }: NodeProps) => {
    const state = useContext(TreeRenderContext)
    const { itemId, direction, isRoot } = data as DomainNodeData

    const isHorizontal = direction === 'LR'
    const hasChildren = (state?.childrenCount[itemId] ?? 0) > 0
    const sourcePosition = isHorizontal ? Position.Right : Position.Bottom
    const targetPosition = isHorizontal ? Position.Left : Position.Top

    return (
        <>
            {!isRoot && (
                <Handle className="wl-tree-handle" type="target" position={targetPosition} id={targetPosition} />
            )}
            {hasChildren && (
                <Handle className="wl-tree-handle" type="source" position={sourcePosition} id={sourcePosition} />
            )}
            {state?.render(itemId, direction, isRoot)}
        </>
    )
})

DomainNode.displayName = 'TreeDomainNode'

const nodeTypes = { domain: DomainNode }

/**
 * Дерево ієрархічної сутності: розкладка entitree-flex, полотно xyflow,
 * доменний вміст картки — зверху через `renderNode`.
 *
 * Компонент навмисно нічого не знає про підрозділи: годиться будь-якому
 * adjacency-списку (структура компанії, категорії, рубрикатор) — звідси й
 * місце в Ship, поруч із Calendar.
 *
 * Без `onMove` вузли не тягаються і не з'єднуються мишею: позиції рахує
 * розкладка, а ієрархію змінюють через картку сутності. З `onMove` картку
 * можна взяти за маркер і відпустити на інший вузол — він стане батьком
 * (ADR 028, рішення 6). Підсвічується лише допустима ціль; відпускання будь-де
 * ще — скасування без запиту.
 */
export const Tree = <T extends TreeItemBase>({
    items,
    renderNode,
    isLoading = false,
    nodeWidth = 280,
    nodeHeight = 150,
    height,
    defaultDirection = 'TB',
    emptyText = 'Немає даних',
    onMove,
    canDrag,
    canDrop,
    dragHandleSelector = `.${TREE_DRAG_HANDLE_CLASS}`,
}: TreeProps<T>) => {
    const [direction, setDirection] = useState<TreeDirection>(defaultDirection)
    const [drag, setDrag] = useState<DragState | null>(null)

    const layout = useMemo(
        () => layoutForest(items, { direction, nodeWidth, nodeHeight, forestSpacing: 80 }),
        [items, direction, nodeWidth, nodeHeight]
    )

    // Керовані позиції: xyflow рухає картку під курсором, а після відпускання
    // (і після кожного перечитування) вона повертається на місце з розкладки.
    // Зміщені позиції прив'язані до розкладки, з якої виросли: нова розкладка
    // одразу їх скидає — без кадру зі старими вузлами (інакше `fitView` на
    // старті вписував би порожнє полотно)
    const [moved, setMoved] = useState<{ base: Node[]; nodes: Node[] } | null>(null)
    const positions = moved !== null && moved.base === layout.nodes ? moved.nodes : layout.nodes

    const byId = useMemo(() => new Map(items.map((item) => [item.id, item])), [items])

    const isMovable = onMove !== undefined

    const draggableIds = useMemo(() => {
        if (!isMovable) return new Set<string>()

        return new Set(items.filter((item) => (canDrag ? canDrag(item) : true)).map((item) => item.id))
    }, [canDrag, isMovable, items])

    const onNodesChange = useCallback(
        (changes: NodeChange[]) => {
            setMoved((current) => {
                const from = current !== null && current.base === layout.nodes ? current.nodes : layout.nodes

                return { base: layout.nodes, nodes: applyNodeChanges(changes, from) }
            })
        },
        [layout.nodes]
    )

    const onNodeDragStart = useCallback<OnNodeDrag>(
        (_event, node) => {
            const item = byId.get(node.id)
            if (!item) return

            const allowed = allowedDropTargets(items, node.id)

            if (canDrop) {
                for (const id of [...allowed]) {
                    const target = byId.get(id)
                    if (!target || !canDrop(item, target)) allowed.delete(id)
                }
            }

            setDrag({ id: node.id, subtree: subtreeIds(items, node.id), allowed, targetId: null })
        },
        [byId, canDrop, items]
    )

    const targetFor = useCallback(
        (node: Node, state: DragState): string | null =>
            findDropTarget(
                { x: node.position.x, y: node.position.y, width: nodeWidth, height: nodeHeight },
                positions
                    .filter((candidate) => state.allowed.has(candidate.id))
                    .map((candidate) => ({
                        id: candidate.id,
                        rect: { x: candidate.position.x, y: candidate.position.y, width: nodeWidth, height: nodeHeight },
                    }))
            ),
        [nodeHeight, nodeWidth, positions]
    )

    const onNodeDrag = useCallback<OnNodeDrag>(
        (_event, node) => {
            setDrag((state) => {
                if (!state || state.id !== node.id) return state

                const targetId = targetFor(node, state)

                return targetId === state.targetId ? state : { ...state, targetId }
            })
        },
        [targetFor]
    )

    const onNodeDragStop = useCallback<OnNodeDrag>(
        (_event, node) => {
            const targetId = drag && drag.id === node.id ? targetFor(node, drag) : null
            const item = byId.get(node.id)
            const target = targetId !== null ? byId.get(targetId) : undefined

            setDrag(null)
            setMoved(null)

            if (item && target && onMove) onMove(item, target)
        },
        [byId, drag, onMove, targetFor]
    )

    const renderState = useMemo<TreeRenderState>(() => {
        const childrenCount: Record<string, number> = {}

        for (const item of items) {
            if (item.parentId !== null && byId.has(item.parentId)) {
                childrenCount[item.parentId] = (childrenCount[item.parentId] ?? 0) + 1
            }
        }

        return {
            childrenCount,
            render: (itemId, nodeDirection, isRoot) => {
                const item = byId.get(itemId)
                if (!item) return null

                const isDragged = drag?.id === itemId

                return renderNode(item, {
                    isRoot,
                    direction: nodeDirection,
                    hasChildren: (childrenCount[itemId] ?? 0) > 0,
                    childrenCount: childrenCount[itemId] ?? 0,
                    isDraggable: draggableIds.has(itemId),
                    isDragged,
                    dragCount: isDragged ? (drag?.subtree.size ?? 0) : 0,
                    isDropTarget: drag?.targetId === itemId,
                })
            },
        }
    }, [byId, drag, draggableIds, items, renderNode])

    const nodes = useMemo<Node[]>(() => {
        if (!isMovable) return layout.nodes

        return positions.map((node) => {
            const classes = [
                drag?.targetId === node.id ? 'wl-tree-drop-target' : '',
                // Нащадки перетягнутої картки їдуть разом із нею — на старому
                // місці вони приглушені, щоб не здавалося, що лишаються
                drag && drag.id !== node.id && drag.subtree.has(node.id) ? 'wl-tree-dimmed' : '',
            ].filter(Boolean)

            return {
                ...node,
                draggable: draggableIds.has(node.id),
                dragHandle: dragHandleSelector,
                ...(classes.length > 0 ? { className: classes.join(' ') } : {}),
            }
        })
    }, [drag, dragHandleSelector, draggableIds, isMovable, layout.nodes, positions])

    const wrapperStyle = { height: height ?? 'calc(100vh - 260px)', width: '100%' }

    if (isLoading) {
        return (
            <div className="wl-tree-placeholder" style={wrapperStyle}>
                <Spin size="large" />
            </div>
        )
    }

    if (!items.length) {
        return (
            <div className="wl-tree-placeholder" style={wrapperStyle}>
                <Empty description={emptyText} />
            </div>
        )
    }

    return (
        <div className="wl-tree" style={wrapperStyle}>
            <TreeRenderContext.Provider value={renderState}>
                <ReactFlow
                    nodes={nodes}
                    edges={layout.edges}
                    nodeTypes={nodeTypes}
                    nodesDraggable={isMovable}
                    nodesConnectable={false}
                    edgesFocusable={false}
                    proOptions={{ hideAttribution: true }}
                    minZoom={0.2}
                    fitView
                    {...(isMovable ? { onNodesChange, onNodeDragStart, onNodeDrag, onNodeDragStop } : {})}
                >
                    <Background gap={16} />
                    <Controls showInteractive={false} />
                    <Panel position="top-right">
                        <Radio.Group
                            value={direction}
                            onChange={(event) => setDirection(event.target.value as TreeDirection)}
                        >
                            <Radio.Button value="TB">Вертикально</Radio.Button>
                            <Radio.Button value="LR">Горизонтально</Radio.Button>
                        </Radio.Group>
                    </Panel>
                </ReactFlow>
            </TreeRenderContext.Provider>
        </div>
    )
}
